import React from 'react';
import { FieldContextOverrides } from '../../../utils/faqApi';

// Full config type — all 11 fields required for editing
export type FullFieldContext = Required<FieldContextOverrides>;

interface StringArrayEditorProps {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

const StringArrayEditor: React.FC<StringArrayEditorProps> = ({ items, onChange, placeholder }) => {
  const update = (index: number, value: string) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...items, '']);
  };

  return (
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder || 'Enter value...'}
            className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={() => remove(i)}
            className="text-gray-400 hover:text-red-500 text-lg leading-none flex-shrink-0"
            title="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="text-sm text-blue-600 hover:text-blue-800 hover:underline mt-1"
      >
        + Add item
      </button>
    </div>
  );
};

interface FieldRowProps {
  label: string;
  fieldKey: keyof FullFieldContext;
  isChanged: boolean;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, isChanged, children }) => (
  <div
    className={`pb-4 border-b border-gray-100 last:border-0 ${
      isChanged ? 'border-l-2 border-l-amber-400 pl-3 bg-amber-50 rounded-r' : ''
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}
      </label>
      {isChanged && (
        <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-medium">
          modified
        </span>
      )}
    </div>
    {children}
  </div>
);

interface FieldEditorProps {
  config: FullFieldContext;
  canonical: FullFieldContext | null;
  onChange: (updated: FullFieldContext) => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({ config, canonical, onChange }) => {
  const set = <K extends keyof FullFieldContext>(key: K, value: FullFieldContext[K]) => {
    onChange({ ...config, [key]: value });
  };

  const isChanged = (key: keyof FullFieldContext): boolean => {
    if (!canonical) return false;
    const cv = canonical[key];
    const ev = config[key];
    return JSON.stringify(cv) !== JSON.stringify(ev);
  };

  return (
    <div className="space-y-4">
      <FieldRow label="Field Type" fieldKey="fieldType" isChanged={isChanged('fieldType')}>
        <input
          type="text"
          value={config.fieldType}
          onChange={(e) => set('fieldType', e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </FieldRow>

      <FieldRow label="Field Description" fieldKey="fieldDescription" isChanged={isChanged('fieldDescription')}>
        <textarea
          rows={2}
          value={config.fieldDescription}
          onChange={(e) => set('fieldDescription', e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />
      </FieldRow>

      <FieldRow label="Significance" fieldKey="significance" isChanged={isChanged('significance')}>
        <textarea
          rows={2}
          value={config.significance}
          onChange={(e) => set('significance', e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />
      </FieldRow>

      <FieldRow label="Common Rejection Reasons" fieldKey="commonRejectionReasons" isChanged={isChanged('commonRejectionReasons')}>
        <StringArrayEditor
          items={config.commonRejectionReasons}
          onChange={(v) => set('commonRejectionReasons', v)}
          placeholder="e.g. Failure to report required contacts"
        />
      </FieldRow>

      <FieldRow label="Interview Questions" fieldKey="interviewQuestions" isChanged={isChanged('interviewQuestions')}>
        <StringArrayEditor
          items={config.interviewQuestions}
          onChange={(v) => set('interviewQuestions', v)}
          placeholder="e.g. What is the nature of this relationship?"
        />
      </FieldRow>

      <FieldRow label="Final Prompt" fieldKey="finalPrompt" isChanged={isChanged('finalPrompt')}>
        <textarea
          rows={2}
          value={config.finalPrompt}
          onChange={(e) => set('finalPrompt', e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />
      </FieldRow>

      <FieldRow label="Help Text" fieldKey="helpText" isChanged={isChanged('helpText')}>
        <input
          type="text"
          value={config.helpText}
          onChange={(e) => set('helpText', e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </FieldRow>

      <FieldRow label="Best Practices" fieldKey="bestPractices" isChanged={isChanged('bestPractices')}>
        <StringArrayEditor
          items={config.bestPractices}
          onChange={(v) => set('bestPractices', v)}
          placeholder="e.g. Review SEAD-3 Section F.2 guidance"
        />
      </FieldRow>

      <FieldRow label="Warning Flags" fieldKey="warningFlags" isChanged={isChanged('warningFlags')}>
        <StringArrayEditor
          items={config.warningFlags}
          onChange={(v) => set('warningFlags', v)}
          placeholder="e.g. Saying 'I don't know' without further details"
        />
      </FieldRow>

      <FieldRow label="Expected Length" fieldKey="expectedLength" isChanged={isChanged('expectedLength')}>
        <select
          value={config.expectedLength}
          onChange={(e) => set('expectedLength', e.target.value as 'brief' | 'moderate' | 'detailed')}
          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="brief">Brief</option>
          <option value="moderate">Moderate</option>
          <option value="detailed">Detailed</option>
        </select>
      </FieldRow>

      <FieldRow label="Requires Specificity" fieldKey="requiresSpecificity" isChanged={isChanged('requiresSpecificity')}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.requiresSpecificity}
            onChange={(e) => set('requiresSpecificity', e.target.checked)}
            className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">
            {config.requiresSpecificity ? 'Yes — requires specific details' : 'No — general guidance sufficient'}
          </span>
        </label>
      </FieldRow>
    </div>
  );
};

export default FieldEditor;
